import { ColumnHeader } from "@/deduction_table/column_header"
import {
  categoryDescriptionColumn,
  checkNumberColumn,
  dcColumn,
  DeductionTableColumn,
  customerColumn,
  daysInStageColumn,
  daysOldColumn,
  disputeExpiresColumn,
  filesColumn,
  backupParseColumn,
  promotionMatchColumn,
  importSourceColumn,
  customDeductionMethodColumn,
  invoiceNumberColumn,
  retailerColumn,
  selectColumn,
  checkAmountColumn,
  TableColumn,
} from "@/deduction_table/columns"
import { createOrgColumns, OrgColumnConfig } from "@/shared/org_columns.tsx"
import { is_cleared_status, month_list_field_value } from "@/shared/clearing_list.ts"
import { FieldType, OrgFieldConfig } from "@/api/org_field_config.tsx"
import { CategoryCell } from "@/deduction_table"
import { CategoryFilterHeader } from "@/deduction_table/CategoryFilterHeader.tsx"
import { cellDateFormatter, createDateColumn } from "@/deduction_table/date_column"
import { Badge } from "@/components/ui/badge.tsx"
import { cellCurrencyFormatter } from "@/deduction/[id]/backup_columns.tsx"
import { exactCurrencyFormatter } from "@/utils"
import { display_accounting_amount } from "@/utils/transaction_amount"
import { defaultFilter } from "@/utils/filters.ts"
import { Split, TransactionJson } from "@/api"
import { DeductionListJson } from "@/api/deduction.tsx"
import { Column, ColumnDef } from "@tanstack/react-table"
import { AccountingState } from "@/transactions/state.tsx"
import { StatusSelect } from "@/deduction_table/status_select.tsx"
import {
  StageMultiSelect,
  parse_status_column_filter,
  serialize_status_column_filter,
  stages_equal,
} from "@/deduction_table/stage_multi_select.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { Label } from "@/components/ui/label.tsx"

function getReasonCodeNames(splits: Split[]) {
  const uniqueNames = [...new Set(splits.map(code => code.expense_account || code.name).filter(name => name !== ""))]
  return uniqueNames.join(", ")
}

// const getExpenseAccounts = (codes?: Array<{ expense_account?: string }>) => {
//   if (!codes?.length) return ""
//
//   // Filter out undefined/empty expense accounts and trim remaining ones
//   const validCodes = codes
//     .filter(code => code.expense_account)
//     .map(code => ({
//       ...code,
//       expense_account: code.expense_account!.trim(),
//     }))
//
//   if (!validCodes.length) return ""
//
//   const uniquePrefixes = [...new Set(validCodes.map(code => code.expense_account.split(":")[0]))]
//
//   // If only one valid code or all codes share the same prefix, return the full first valid account
//   if (uniquePrefixes.length === 1) {
//     return validCodes[0].expense_account
//   }
//
//   // Otherwise return concatenated prefixes
//   return uniquePrefixes.join(", ")
// }

export const poNumberColumn: DeductionTableColumn = {
  accessorKey: "po_number",
  header: ({ column }) => <ColumnHeader column={column} title="PO " />,
  enableSorting: true,
  filterFn: defaultFilter,
}

export const remoteAccountingIdColumn: DeductionTableColumn = {
  accessorKey: "remote_accounting_id",
  header: ({ column }) => <ColumnHeader column={column} title="Accounting ID" />,
  enableSorting: true,
  filterFn: defaultFilter,
}

export const pendingColumn: DeductionTableColumn = {
  accessorKey: "pending",
  header: ({ column }) => <ColumnHeader column={column} title="Pending" />,
  enableSorting: true,
  cell: ({ row }) => {
    const pending = row.original.pending
    if (!pending) return null
    return <Badge variant="pending">Pending</Badge>
  },
  filterFn: (row, id, value) => {
    return row.original.pending === value
  },
}

// export const expenseAccountColumn: DeductionTableColumn = {
//   id: "expense_account",
//   accessorFn: row => getExpenseAccounts(row.reason_codes),
//   header: ({ column }) => <ColumnHeader column={column} title="Expense Account" />,
//   enableSorting: true,
//   cell: ({ row }) => getExpenseAccounts(row.original.reason_codes),
//   filterFn: defaultFilter,
// }

export const reasonCodeColumn: ColumnDef<TransactionJson> = {
  id: "reason_code",
  accessorFn: row => getReasonCodeNames(row.splits),
  header: ({ column }) => <ColumnHeader column={column} title="Reason Code" />,
  enableSorting: true,
  cell: ({ row }) => getReasonCodeNames(row.original.splits),
  filterFn: defaultFilter,
}

// Custom category column for accounting table that uses AccountingState
export const accountingCategoryColumn: DeductionTableColumn = {
  accessorKey: "category",
  header: ({ column }) => (
    <CategoryFilterHeader
      column={column}
      onSelectChoice={(choice: string) => {
        AccountingState.set(s => {
          let columnFilters = s.columnFilters.filter(f => f.id !== "category")
          if (choice !== "all") {
            columnFilters.push({ id: "category", value: choice })
          }
          return { ...s, columnFilters }
        })
      }}
    />
  ),
  cell: ({ row }) => <CategoryCell deduction={row.original} />,
  filterFn: defaultFilter,
}

function AccountingStageColumnFilter({ column }: { column: Column<DeductionListJson, unknown> }) {
  const accountingState = AccountingState.use()
  const status_filter = accountingState.columnFilters.find(f => f.id === "status_value")?.value
  return (
    <ColumnHeader
      column={column}
      title="Stage"
      filterContent={
        <StageMultiSelect
          selected={parse_status_column_filter(status_filter)}
          onChange={stages => {
            const value = serialize_status_column_filter(stages)
            AccountingState.set(s => ({
              ...s,
              columnFilters: [
                ...s.columnFilters.filter(f => f.id !== "status_value"),
                ...(value != null ? [{ id: "status_value", value }] : []),
              ],
            }))
          }}
        />
      }
    />
  )
}

/**
 * Cleared state for the transactions view. "Cleared" means fully done: the deduction is in a
 * settled stage right now (validated, or won — see `is_cleared_status`) AND its GL coding is
 * complete. "Validated (Uncoded)" is approved by the sales team but still missing coding — it
 * can't be expensed yet. "Pending" is everything else (waiting on backup, sales-team approval, or a
 * dispute). Only the current stage counts: `validated_date` records the *first* validation and
 * survives a later move back to Review or Disputed, and reading it here is what showed Saffron
 * Road rows as both "Disputed" and "Cleared" in their August 2026 close.
 */
export function transaction_cleared_value(row: {
  validated?: boolean
  status_value?: string
  coded?: boolean
}): "Cleared" | "Validated (Uncoded)" | "Pending" {
  const validated = Boolean(row.validated) || is_cleared_status(row.status_value)
  if (validated && row.coded) return "Cleared"
  if (validated) return "Validated (Uncoded)"
  return "Pending"
}

const CLEARED_BADGE_VARIANTS = {
  "Cleared": "success",
  "Validated (Uncoded)": "amber",
  "Pending": "warning",
} as const

export const CLEARED_STATES = ["Cleared", "Validated (Uncoded)", "Pending"] as const

/** "Open" = everything not fully cleared (Saffron's definition: waiting on approval or coding). */
export const OPEN_CLEARED_STATES: string[] = ["Validated (Uncoded)", "Pending"]

/** True when a `cleared` column filter value matches the "open" selection (order-independent). */
export function is_open_cleared_filter(value: unknown): boolean {
  return stages_equal(parse_status_column_filter(value), OPEN_CLEARED_STATES)
}

/** Replace the `cleared` column filter in AccountingState (null selection = no filter). */
export function set_cleared_column_filter(states: string[] | null) {
  const value = serialize_status_column_filter(states)
  AccountingState.set(s => ({
    ...s,
    columnFilters: [
      ...s.columnFilters.filter(f => f.id !== "cleared"),
      ...(value != null ? [{ id: "cleared", value }] : []),
    ],
  }))
}

interface ClearedMultiSelectProps {
  /** null = all states (no restriction). */
  selected: string[] | null
  onChange: (states: string[] | null) => void
}

/** Structured filter for the Cleared column: All/Open presets + one checkbox per state. */
export function ClearedMultiSelect({ selected, onChange }: ClearedMultiSelectProps) {
  const openActive = selected != null && stages_equal(selected, OPEN_CLEARED_STATES)

  function toggle_state(state: string, checked: boolean) {
    const current = selected ?? [...CLEARED_STATES]
    const next = checked ? [...new Set([...current, state])] : current.filter(v => v !== state)
    onChange(next.length === CLEARED_STATES.length ? null : next)
  }

  return (
    <div class="w-56">
      <div class="mb-3 flex flex-wrap gap-1">
        <Button
          variant={selected == null ? "default" : "outline"}
          size="sm"
          className="h-7 text-xs"
          onClick={() => onChange(null)}>
          All
        </Button>
        <Button
          variant={openActive ? "default" : "outline"}
          size="sm"
          className="h-7 text-xs"
          onClick={() => onChange([...OPEN_CLEARED_STATES])}>
          Open
        </Button>
      </div>
      <div class="space-y-1">
        {CLEARED_STATES.map(state => (
          <Label key={state} className="flex items-center gap-2 py-0.5 font-normal">
            <Checkbox
              checked={selected == null || selected.includes(state)}
              onCheckedChange={(checked: boolean | string) => toggle_state(state, !!checked)}
            />
            {state}
          </Label>
        ))}
      </div>
    </div>
  )
}

function ClearedColumnFilter({ column }: { column: Column<TransactionJson, unknown> }) {
  const accountingState = AccountingState.use()
  const cleared_filter = accountingState.columnFilters.find(f => f.id === "cleared")?.value
  return (
    <ColumnHeader
      column={column}
      title="Cleared"
      filterContent={
        <ClearedMultiSelect
          selected={parse_status_column_filter(cleared_filter)}
          onChange={set_cleared_column_filter}
        />
      }
    />
  )
}

export const clearedColumn: ColumnDef<TransactionJson> = {
  id: "cleared",
  accessorFn: row => transaction_cleared_value(row),
  header: ({ column }) => <ClearedColumnFilter column={column} />,
  enableSorting: true,
  cell: ({ row }) => {
    const value = transaction_cleared_value(row.original)
    return (
      <div class="flex justify-center">
        <Badge variant={CLEARED_BADGE_VARIANTS[value]}>{value}</Badge>
      </div>
    )
  },
  filterFn: defaultFilter,
}

export const accountingStatusColumn: DeductionTableColumn = {
  accessorKey: "status_value",
  header: ({ column }) => <AccountingStageColumnFilter column={column} />,
  cell: cell => {
    if (cell.row.original.kind === "prepayment") {
      return null
    } else {
      return (
        <div class="flex justify-center">
          <StatusSelect deduction={cell.row.original} />
        </div>
      )
    }
  },
  filterFn: defaultFilter,
}

// export const reasonCodeActorColumn: DeductionTableColumn = {
//   id: "reason_code_actor",
//   accessorFn: row => {
//     const primaryCode = row.reason_codes?.[0]
//     return primaryCode?.actor || ""
//   },
//   header: ({ column }) => <ColumnHeader column={column} title="Coded by" />,
//   enableSorting: true,
//   cell: ({ row }) => row.original.reason_codes?.[0]?.actor,
//   filterFn: defaultFilter,
// }

export const invoiceAmountColumn: DeductionTableColumn = {
  accessorKey: "invoice_amount",
  enableHiding: false,
  header: ({ column }) => <ColumnHeader column={column} title="Amount" />,
  cell: ({ row }) => (
    <div class="text-right font-medium">
      {exactCurrencyFormatter(display_accounting_amount(row.original.kind, row.original.invoice_amount), row.original.currency)}
    </div>
  ),
  sortingFn: (rowA, rowB) => {
    let a = rowA.original.invoice_amount
    let b = rowB.original.invoice_amount
    return Math.abs(a) - Math.abs(b)
  },
}

export function createAccountingOrgColumns(
  orgId: string,
  fieldConfigs: OrgFieldConfig[],
  tableContext: string = "accounting_table"
): TableColumn<TransactionJson>[] {
  const config: OrgColumnConfig<TransactionJson> = {
    tableContext,
    getValue: (row, fieldName) => {
      const month_list = month_list_field_value(fieldName, row)
      if (month_list !== null) {
        return month_list
      }
      if (fieldName === "promotion_remote_ids") {
        return row.promotion_remote_ids || ""
      }
      // Transaction-level fields live directly on the row rather than on a GL split.
      if (
        fieldName === "posting_date" ||
        fieldName === "validated_date" ||
        fieldName === "remote_accounting_id" ||
        fieldName === "pushed_at"
      ) {
        return (row as any)[fieldName] ?? ""
      }
      if (fieldName === "is_trade") {
        const v = row.splits?.[0]?.is_trade
        return v === true ? "true" : v === false ? "false" : ""
      }
      // Everything else (incl. promotion_start_date / promotion_end_date) comes from the split.
      const firstSplit = row.splits?.[0]
      return firstSplit?.[fieldName as keyof Split] ?? ""
    },
    getOrgId: row => row.org_id,
    // The accounting table already carries `remoteAccountingIdColumn`; the check detail table
    // builds its own columns and does not, so there it is the org column's job.
    staticFieldNames:
      tableContext === "accounting_table" ? ["retailer_name", "remote_accounting_id"] : ["retailer_name"],
  }

  return createOrgColumns(orgId, fieldConfigs, config) as TableColumn<TransactionJson>[]
}

export const ACCOUNTING_COLUMNS_WITHOUT_BACKUP_PARSE: TableColumn<TransactionJson>[] = [
  selectColumn as TableColumn<TransactionJson>,
  // todo(joey): filter dates by check_date on accounting view
  createDateColumn("check_date", "Check Date", {
    enableGlobalFilter: true,
    enableHiding: false,
  }) as TableColumn<TransactionJson>,
  createDateColumn("invoice_date", "Invoice Date") as TableColumn<TransactionJson>,
  invoiceNumberColumn as TableColumn<TransactionJson>,
  checkNumberColumn as TableColumn<TransactionJson>,
  customerColumn as TableColumn<TransactionJson>,
  retailerColumn as TableColumn<TransactionJson>,
  categoryDescriptionColumn as TableColumn<TransactionJson>,
  invoiceAmountColumn as TableColumn<TransactionJson>,
  checkAmountColumn as TableColumn<TransactionJson>,
  filesColumn as TableColumn<TransactionJson>,
  accountingStatusColumn as TableColumn<TransactionJson>,
  clearedColumn as TableColumn<TransactionJson>,
  // expenseAccountColumn as TableColumn<TransactionListJson>,
  accountingCategoryColumn as TableColumn<TransactionJson>,
  reasonCodeColumn as TableColumn<TransactionJson>,
  promotionMatchColumn as TableColumn<TransactionJson>,
  // reasonCodeActorColumn,

  {
    id: "execution_date",
    accessorFn: row => (row as any).execution_date || row.splits.find(s => s.execution_date)?.execution_date || "",
    header: ({ column }) => <ColumnHeader column={column} title="Execution Date" />,
    enableSorting: true,
    cell: cellDateFormatter,
    // @ts-ignore
    filterFn: "dateFilter" as const,
  },

  dcColumn as TableColumn<TransactionJson>,
  poNumberColumn as TableColumn<TransactionJson>,
  daysOldColumn as TableColumn<TransactionJson>,
  daysInStageColumn as TableColumn<TransactionJson>,
  disputeExpiresColumn as TableColumn<TransactionJson>,
  importSourceColumn as TableColumn<TransactionJson>,
  customDeductionMethodColumn as TableColumn<TransactionJson>,
  pendingColumn as TableColumn<TransactionJson>,
  remoteAccountingIdColumn as TableColumn<TransactionJson>,
  {
    accessorKey: "kind",
    enableHiding: true,
    filterFn: (row, id, value) => {
      if (value === "all") return true
      return row.original.kind === value
    },
  },
  {
    accessorKey: "coded",
    enableHiding: true,
    filterFn: (row, id, value) => {
      if (value === null || value === undefined) return true
      return row.original.coded === value
    },
  },
  {
    accessorKey: "backup_since_coding",
    enableHiding: true,
    filterFn: (row, id, value) => {
      if (value !== true) return true
      return row.original.backup_since_coding === true
    },
  },
]

export const ACCOUNTING_COLUMNS: TableColumn<TransactionJson>[] = [
  ...ACCOUNTING_COLUMNS_WITHOUT_BACKUP_PARSE,
  backupParseColumn as TableColumn<TransactionJson>,
]

if (import.meta.vitest) {
  describe("Reason code formatting utilities", () => {
    const createMockReasonCode = (name: string) => ({ name }) as Split

    describe("getReasonCodeNames", () => {
      it("should deduplicate reason codes", () => {
        const codes: Split[] = [
          createMockReasonCode("Code1"),
          createMockReasonCode("Code2"),
          createMockReasonCode("Code1"),
        ]
        expect(getReasonCodeNames(codes)).toBe("Code1, Code2")
      })

      it("should handle empty array", () => {
        expect(getReasonCodeNames([])).toBe("")
      })

      it("should handle array with empty or null names", () => {
        const codes = [createMockReasonCode(""), createMockReasonCode("Code1"), { name: "" } as Split]
        expect(getReasonCodeNames(codes)).toBe("Code1")
      })
    })
  })

  describe("accounting columns", () => {
    it("includes the shared promotion match column", () => {
      expect(ACCOUNTING_COLUMNS.some((c: any) => c.id === "promotion_match")).toBe(true)
    })

    it("includes the cleared column", () => {
      expect(ACCOUNTING_COLUMNS.some((c: any) => c.id === "cleared")).toBe(true)
    })
  })

  describe("cleared column filter", () => {
    it("recognizes the open selection order-independently", () => {
      expect(is_open_cleared_filter("Validated (Uncoded),Pending")).toBe(true)
      expect(is_open_cleared_filter("Pending,Validated (Uncoded)")).toBe(true)
      expect(is_open_cleared_filter("Pending")).toBe(false)
      expect(is_open_cleared_filter(undefined)).toBe(false)
    })

    it("open selection matches Pending and Validated (Uncoded) rows via defaultFilter, not Cleared", () => {
      const value = OPEN_CLEARED_STATES.join(",")
      const row_with = (cell: string) => ({ getValue: () => cell })
      expect(defaultFilter(row_with("Pending"), "cleared", value)).toBe(true)
      expect(defaultFilter(row_with("Validated (Uncoded)"), "cleared", value)).toBe(true)
      expect(defaultFilter(row_with("Cleared"), "cleared", value)).toBe(false)
    })
  })

  describe("transaction_cleared_value", () => {
    it("is Cleared only when validated AND coded", () => {
      expect(transaction_cleared_value({ validated: true, status_value: "validated", coded: true })).toBe("Cleared")
    })

    it("counts a won row (disputed and repaid) as cleared", () => {
      expect(transaction_cleared_value({ validated: false, status_value: "won", coded: true })).toBe("Cleared")
    })

    it("goes back to Pending when a validated row is disputed or sent to review", () => {
      // The first-validation date is still on the row; only the current stage matters.
      expect(transaction_cleared_value({ validated: false, status_value: "disputed", coded: true })).toBe("Pending")
      expect(transaction_cleared_value({ validated: false, status_value: "review", coded: true })).toBe("Pending")
    })

    it("is Validated (Uncoded) when approved but GL coding is incomplete", () => {
      expect(transaction_cleared_value({ validated: true, status_value: "validated", coded: false })).toBe(
        "Validated (Uncoded)"
      )
    })

    it("is Pending for everything not validated, even if coded", () => {
      expect(transaction_cleared_value({ validated: false, status_value: "new", coded: true })).toBe("Pending")
      expect(transaction_cleared_value({})).toBe("Pending")
    })
  })

  describe("createAccountingOrgColumns", () => {
    it("computes clearing_list from the posting month, cleared or not", () => {
      const columns = createAccountingOrgColumns("org", [
        {
          id: "1",
          org_id: "org",
          field_name: "clearing_list",
          field_type: FieldType.Text,
          display_name: "Clearing List",
          display_order: 0,
          is_active: true,
          table_contexts: ["accounting_table"],
          created_at: "",
          updated_at: "",
        },
      ] as OrgFieldConfig[])

      const accessor = (columns[0] as any).accessorFn
      expect(
        accessor({ org_id: "org", status_value: "validated", validated_date: "2026-06-25", posting_date: "2026-06-20", splits: [] })
      ).toBe("June 2026")
      // Disputed after validation: open again, but still a June deduction.
      expect(
        accessor({ org_id: "org", status_value: "disputed", validated_date: "2026-06-25", posting_date: "2026-06-20", splits: [] })
      ).toBe("June 2026")
    })

    const accounting_id_config = (context: string) =>
      [{
        id: "1",
        org_id: "org",
        field_name: "remote_accounting_id",
        field_type: FieldType.Text,
        display_name: "ARCR",
        display_order: 0,
        is_active: true,
        table_contexts: [context],
        created_at: "",
        updated_at: "",
      }] as OrgFieldConfig[]

    it("does not duplicate the accounting table's static Accounting ID column", () => {
      // Both would carry id "remote_accounting_id"; tanstack cannot hold two columns with one id.
      expect(createAccountingOrgColumns("org", accounting_id_config("accounting_table"))).toHaveLength(0)
      expect(ACCOUNTING_COLUMNS.some((c: any) => c.accessorKey === "remote_accounting_id")).toBe(true)
    })

    it("still builds the column for the check detail table, which has no static one", () => {
      const columns = createAccountingOrgColumns("org", accounting_id_config("check_detail_table"), "check_detail_table")
      expect(columns).toHaveLength(1)
      // Reads the row, not the first split, which is where the fall-through would have looked.
      expect(
        (columns[0] as any).accessorFn({ org_id: "org", remote_accounting_id: "ARCR-1", splits: [{}] })
      ).toBe("ARCR-1")
    })

    it("reads configured pushed_at from the transaction row", () => {
      const config = [{
        ...accounting_id_config("accounting_table")[0],
        field_name: "pushed_at",
        field_type: FieldType.Date,
        display_name: "Pushed Date",
      }] as OrgFieldConfig[]
      const [column] = createAccountingOrgColumns("org", config)

      expect(
        (column as any).accessorFn({ org_id: "org", pushed_at: "2026-08-15T12:00:00Z", splits: [] })
      ).toBe("2026-08-15T12:00:00Z")
    })
  })

  describe("backup_since_coding filter", () => {
    const column = ACCOUNTING_COLUMNS.find((c: any) => c.accessorKey === "backup_since_coding") as any
    function keeps(row: Partial<TransactionJson>, value: unknown): boolean {
      return column.filterFn({ original: row }, "backup_since_coding", value)
    }

    it("keeps only flagged rows while active", () => {
      expect(keeps({ backup_since_coding: true }, true)).toBe(true)
      expect(keeps({ backup_since_coding: false }, true)).toBe(false)
      // Older responses without the field read as not flagged.
      expect(keeps({}, true)).toBe(false)
    })

    it("keeps everything when inactive", () => {
      expect(keeps({ backup_since_coding: false }, undefined)).toBe(true)
      expect(keeps({ backup_since_coding: false }, null)).toBe(true)
    })
  })
}
